import { postConnectionGetGcpConnectionUrl, postConnectionGetGcpAccessTokenFromCode } from '@apis/Resources';
import { GcpAuthService } from '@root/Services/Connections/GcpAuthService';
import { useDi } from '@root/Services/DI';
import { GcpToken } from '@apis/Resources/model';
import { useEffect } from 'react';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { container } from 'tsyringe';

interface AuthCallbackMessage {
    type: 'GCP_AUTH_CALLBACK';
    code?: string;
    state?: string;
    error?: string;
}

interface GcpConnectionUrl {
    authorizeUrl: string;
    state: string;
    verifier: string;
}

let pendingState: string | null = null;
let authWindow: Window | null = null;
let messageHandler: ((event: MessageEvent) => void) | null = null;
let pendingAuthPromise: Promise<string> | null = null;

export function AuthenticateGcp(): Promise<string> {
    const gcpAuthSvc = container.resolve(GcpAuthService);

    if (typeof window === 'undefined') {
        return Promise.reject(new Error('GCP auth popup can only run in a browser environment.'));
    }

    pendingAuthPromise = new Promise<string>(async (resolve, reject) => {
        try {
            const urlResponse = await postConnectionGetGcpConnectionUrl();
            const { authorizeUrl, state, verifier } = urlResponse as unknown as GcpConnectionUrl;

            pendingState = state;

            if (messageHandler) {
                window.removeEventListener('message', messageHandler);
            }

            messageHandler = async (event: MessageEvent) => {
                if (event.origin !== window.location.origin) {
                    return;
                }

                const message = event.data as AuthCallbackMessage;

                if (message.type !== 'GCP_AUTH_CALLBACK') {
                    return;
                }

                window.removeEventListener('message', messageHandler!);
                messageHandler = null;

                if (authWindow && !authWindow.closed) {
                    authWindow.close();
                }
                authWindow = null;

                if (message.error) {
                    pendingState = null;
                    reject(new Error(`GCP authentication error: ${message.error}`));
                    return;
                }

                if (!message.code || !message.state) {
                    pendingState = null;
                    reject(new Error('Missing code or state in callback'));
                    return;
                }

                const messageState = message.state?.split(':')[0];
                if (messageState !== pendingState) {
                    pendingState = null;
                    reject(new Error('State mismatch - possible CSRF attack'));
                    return;
                }

                pendingState = null;

                try {
                    const tokenResponse = await ExchangeCodeForToken(message.code, verifier);

                    if (!tokenResponse || !tokenResponse.AccessToken) {
                        reject(new Error('Failed to retrieve access token'));
                        return;
                    }

                    let expiresIn = 3600;
                    if (tokenResponse.Expires) {
                        const expiresDate = new Date(tokenResponse.Expires);
                        const now = new Date();
                        const diffSeconds = Math.floor((expiresDate.getTime() - now.getTime()) / 1000);
                        if (diffSeconds > 0) {
                            expiresIn = diffSeconds;
                        }
                    }

                    gcpAuthSvc.setTokens(tokenResponse.AccessToken, expiresIn);

                    resolve(tokenResponse.AccessToken);
                } catch (error) {
                    reject(error);
                }
            };

            window.addEventListener('message', messageHandler);

            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            authWindow = window.open(
                authorizeUrl,
                'GCP Authentication',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
            );

            if (!authWindow) {
                window.removeEventListener('message', messageHandler);
                messageHandler = null;
                reject(new Error('Failed to open authentication window. Please allow popups.'));
                return;
            }

            const checkClosed = setInterval(() => {
                if (authWindow && authWindow.closed) {
                    clearInterval(checkClosed);
                    if (messageHandler) {
                        window.removeEventListener('message', messageHandler);
                        messageHandler = null;
                    }
                    pendingState = null;
                    reject(new Error('Authentication window was closed'));
                }
            }, 500);
        } catch (error) {
            if (messageHandler) {
                window.removeEventListener('message', messageHandler);
                messageHandler = null;
            }
            pendingState = null;
            reject(error);
        }
    });

    return pendingAuthPromise;
}

async function ExchangeCodeForToken(code: string, verifier: string): Promise<GcpToken> {
    return await postConnectionGetGcpAccessTokenFromCode({ code, verifier });
}

export async function EnsureGcpAuthenticated(): Promise<string> {
    const gcpAuthSvc = container.resolve(GcpAuthService);

    const existingToken = gcpAuthSvc.getAccessToken();
    if (existingToken) {
        return Promise.resolve(existingToken);
    }

    return AuthenticateGcp();
}

export function GcpCallback() {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code') || undefined;
        const state = params.get('state') || undefined;
        const error = params.get('error') || undefined;

        if (window.opener) {
            window.opener.postMessage(
                {
                    type: 'GCP_AUTH_CALLBACK',
                    code,
                    state,
                    error,
                },
                window.location.origin
            );
        }

        setTimeout(() => {
            window.close();
        }, 500);
    }, []);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                background: '#f5f5f5',
            }}
        >
            <div
                style={{
                    textAlign: 'center',
                    padding: '2rem',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
            >
                <h2 style={{ color: '#333', marginBottom: '0.5rem' }}>Authentication Complete</h2>
                <p style={{ color: '#666' }}>This window will close automatically...</p>
            </div>
        </div>
    );
}
endpoint('_gcp-callback', GcpCallback, 'Connection Successful');
